import React, { useState, useEffect } from "react";
import { Search, Tables } from "@deeptrue-frontend/ui";
import Menu from "../Menu";
import styled from "styled-components/macro";
import { useDebounce } from "hooks/useDebouce";
import RouterProvider from "components/RouterProvider/RouterProvider";
import { RouteComponentProps } from "react-router-dom";
import queryString from "query-string";
import { defaultLimit } from "pages/OrganizationAdmin/Projects";
import { useFormatMessage } from "components/IntlProvider";
import Product from "./Product";
import { ProductType } from "pages/OrganizationAdmin/types";

const { ROUTES, withRouter } = RouterProvider;

const AllProjects = styled.span`
  fontsize: 12px;
  color: gray;
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  width: 100%;
  align-items: center;
`;

const { Table, TableBody, TableHeader, TablePagination, TableFooter } = Tables;

const thCells = (t: any) => [
  {
    text: t("organization.columns.name", "Name"),
    orderName: "name",
    props: {
      width: "250px",
      padding: "1rem 0.5rem",
      height: "3rem !important",
    },
  },
  {
    text: t("organization.columns.workbench", "Workbench"),
    orderName: "workbench__name",
    props: {
      width: "250px",
      padding: "1rem 0.5rem",
      height: "3rem !important",
    },
  },
  {
    text: t("organization.columns.groups", "Groups"),
    props: {
      minWidth: "150px",
      padding: "1rem",
      height: "3rem !important",
    },
  },

  {
    text: t("organization.columns.resources", "Resources"),
    props: {
      minWidth: "80px",
      padding: "1rem 0.5rem",
      height: "3rem !important",
    },
  },
];

interface Props extends RouteComponentProps {
  isLoaded: boolean;
  count: number;
  currentPage: number;
  orderBy?: string;
  sortColumns: (colName: string) => void;
  products: ProductType[];
}

const ProductsList = ({
  isLoaded,
  count,
  currentPage,
  history,
  orderBy,
  sortColumns,
  location,
  products,
}: Props) => {
  const t = useFormatMessage();
  const [search, setSearchValue] = useState("");
  const searchValue = useDebounce(search);
  const parsedSearch = queryString.parse(location.search);

  useEffect(() => {
    const params = {
      limit: defaultLimit,
      offset: 0,
      search: searchValue,
      ordering: parsedSearch.ordering,
    };
    history.push(
      `${ROUTES.ORGANIZATION.ADMIN.PRODUCTS}?${queryString.stringify(params)}`
    );
    // eslint-disable-next-line
  }, [searchValue]);

  const onChangePage = (page: number) => {
    const params = {
      limit: defaultLimit,
      offset: defaultLimit * page - defaultLimit,
      search: searchValue,
      ordering: parsedSearch.ordering,
    };
    history.push(
      `${ROUTES.ORGANIZATION.ADMIN.PRODUCTS}?${queryString.stringify(params)}`
    );
  };

  return (
    <>
      <Menu />
      <Search
        placeholder={t("organization.search", "Search...")}
        onChange={(e) => setSearchValue(e.target.value as string)}
      />

      <HeaderWrapper>
        <AllProjects>
          {count}{" "}
          {t("organization.withinOrganization", "{name} withing organization", {
            name: t("organization.products", "Products"),
          })}
        </AllProjects>
      </HeaderWrapper>
      <Table>
        <TableHeader
          cells={thCells(t)}
          orderBy={orderBy}
          onOrdering={sortColumns}
        />
        <TableBody
          withLoader
          isLoading={!isLoaded}
          isData={count > 0}
          noDataText={t(
            "noresults",
            "No results. We have nothing to show here."
          )}
        >
          {products.map((product: ProductType) => {
            return (
              <Product
                key={product.id}
                name={product.name}
                id={product.id as number}
                workbench={product.workbench.name}
                sharedGroups={product.shared_to_groups}
              />
            );
          })}
        </TableBody>

        <TableFooter>
          <TablePagination
            count={count}
            currentPage={currentPage}
            rowsPerPage={defaultLimit}
            onChangePage={onChangePage}
          />
        </TableFooter>
      </Table>
    </>
  );
};

export default withRouter(ProductsList);
